import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

/**
 * A page title that renames in place: click to edit, Enter or blur saves, Escape cancels without a
 * request, and an empty name reverts. It inherits the heading's type, so editing doesn't shift the
 * layout. `onCommit` resolves false when the save failed, which restores the saved name.
 */
export function EditableTitle({
  value,
  onCommit,
  maxLength,
  className,
}: {
  value: string;
  onCommit: (name: string) => Promise<boolean> | boolean;
  maxLength: number;
  className?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  // The new name, shown while the save is in flight so the old one doesn't flash back.
  const [saving, setSaving] = useState<string | null>(null);
  const cancelled = useRef(false);
  useEffect(() => setSaving(null), [value]);

  const shown = saving ?? value;
  const finish = () => {
    setEditing(false);
    if (cancelled.current) {
      cancelled.current = false;
      return;
    }
    const next = draft.trim();
    if (!next || next === value) return;
    setSaving(next);
    void Promise.resolve(onCommit(next)).then((ok) => !ok && setSaving(null));
  };

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => {
          setDraft(shown);
          setEditing(true);
        }}
        className={cn("-mx-1.5 min-w-0 cursor-text truncate rounded-md px-1.5 text-left outline-none transition-colors hover:bg-hover focus-visible:ring-2 focus-visible:ring-ring/40", className)}
      >
        {shown}
      </button>
    );
  }
  return (
    <input
      aria-label="Name"
      autoFocus
      value={draft}
      maxLength={maxLength}
      onFocus={(e) => e.currentTarget.select()}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={finish}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") {
          cancelled.current = true;
          e.currentTarget.blur();
        }
      }}
      className={cn(
        "-mx-1.5 min-w-[4ch] max-w-full rounded-md bg-hover px-1.5 font-[inherit] text-[length:inherit] leading-[inherit] tracking-[inherit] text-foreground outline-none ring-2 ring-ring/30 [field-sizing:content]",
        className,
      )}
    />
  );
}
